import React, { useContext, useEffect, useState } from 'react';
import { ThemeProvider } from 'emotion-theming';
import * as yup from 'yup';
import { Formik } from 'formik';

import { ThemeContext } from '@ordermentum/ui-shared/utilities/theme';
import { Input } from '@ordermentum/ui-shared/atoms/Input';
import { FUTURO_TYPEFACES as F } from '@ordermentum/ui-shared/foundations/font';
import {
  SignedOutContainer,
  ButtonWrapper,
} from '@ordermentum/ui-shared/templates/SignupFlowTemplate/styles';

import Banner from '@ordermentum/ui-shared/components/Banner';
import Button from '@ordermentum/ui-shared/components/Button/Button';
import S from '@ordermentum/ui-shared/foundations/size';
import RightSideContent from '@ordermentum/ui-shared/templates/SignupFlowTemplate/components/RightSideContent';

import { EXTERNAL_LINKS } from '@ordermentum/ui-constants';
import { Icon } from '@ordermentum/ui-shared/foundations';
import {
  Content,
  ErrorContainer,
  InputContainer,
  ThemeStyled,
  getTheme,
} from './styles';
import { Account, Pages } from '..';

type Props = {
  onPageChange: (page: Pages) => void;
  onFormValueChange: (formValues: Account) => void;
  formValues?: Account;
};

const ConsentContainer = ThemeStyled.p(
  ({ theme: { color } }) => ({
    color,
    marginTop: S.L,
    a: {
      padding: 0,
      ...F.BODY_SMALL,
      textDecoration: 'underline',
      display: 'initial',
    },
  }),
  F.BODY_SMALL
);

const initialValues = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
};

const SignUpForm = (props: Props) => {
  const theme = getTheme(useContext(ThemeContext));
  const { onPageChange, onFormValueChange, formValues } = props;
  const [formError, setFormError] = useState<string>('');
  const [initialFormValues, setInitialFormValues] =
    useState<Account>(initialValues);

  const onSubmit = (values: Account) => {
    try {
      onPageChange('venue');
      onFormValueChange(values);
    } catch (err) {
      setFormError(err.response.data.message);
    }
  };

  const validationSchema = yup.object().shape({
    email: yup.string().required(),
    password: yup.string().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
  });

  useEffect(() => {
    if (formValues) {
      setInitialFormValues(formValues);
    } else {
      setInitialFormValues(initialValues);
    }
  }, [formValues]);

  return (
    <ThemeProvider theme={theme}>
      <RightSideContent
        heading="Create an account to trade with this supplier - it's free!"
        subheading="Step 1 / 3"
        footerActions={
          <>
            <SignedOutContainer>
              <Button
                onClick={() => onPageChange('signIn')}
                icon={<Icon name="sChevronLeft" inheritFill />}
                variant="tertiary"
              >
                Back
              </Button>
            </SignedOutContainer>

            <ButtonWrapper>
              <Button variant="primary" form="sign-up-form">
                Continue
              </Button>
            </ButtonWrapper>
          </>
        }
      >
        <Content>
          <Formik
            enableReinitialize={true}
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {formik => (
              <form id="sign-up-form" onSubmit={formik.handleSubmit}>
                <InputContainer>
                  <Input
                    inputLabel="Email"
                    size="max"
                    tag="email"
                    type="email"
                    value={formik.values.email}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="Password"
                    size="max"
                    tag="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="First Name"
                    size="max"
                    tag="firstName"
                    type="text"
                    value={formik.values.firstName}
                    onChange={formik.setFieldValue}
                    required
                  />
                  <Input
                    inputLabel="Last Name"
                    size="max"
                    tag="lastName"
                    type="text"
                    value={formik.values.lastName}
                    onChange={formik.setFieldValue}
                    required
                  />
                </InputContainer>
                <ConsentContainer>
                  By creating an account you agree and consent to our{' '}
                  <Button
                    as="a"
                    target="_blank"
                    href={EXTERNAL_LINKS.LEGAL.TERMS_CONDITIONS}
                    variant="tertiary"
                  >
                    Terms and Conditions
                  </Button>
                  ,{' '}
                  <Button
                    as="a"
                    target="_blank"
                    href={EXTERNAL_LINKS.LEGAL.PRIVACY_COLLECTION_NOTICE}
                    variant="tertiary"
                  >
                    Privacy Collection Notice
                  </Button>{' '}
                  and{' '}
                  <Button
                    as="a"
                    target="_blank"
                    href={EXTERNAL_LINKS.LEGAL.PRIVACY_POLICY}
                    variant="tertiary"
                  >
                    Privacy Policy
                  </Button>
                </ConsentContainer>

                {!!formError && (
                  <ErrorContainer>
                    <Banner variant="error">{formError}</Banner>
                  </ErrorContainer>
                )}
              </form>
            )}
          </Formik>
        </Content>
      </RightSideContent>
    </ThemeProvider>
  );
};

export default SignUpForm;
